import { useContext } from 'react';
import {
	ResponseError,
	StateType,
	TimeStamp,
	FilterPayload,
	TimeStampInfoType,
} from 'contexts/intialstates/Types';
import useApi from './useApi';
import { errorCodes } from 'errorCodes';
import Axios from 'axios';
import { GlobalContexts } from 'contexts/GlobalContext';

export interface InterestRate {
	pageNumber: number;
	pageSize: number;
	totalPages: number;
	totalRecords: number;
	body: InterestRateData[];
}

export interface GetInterestRateProps {
	interestRate: StateType<InterestRate>;
	setInterestRate: React.Dispatch<React.SetStateAction<StateType<InterestRate>>>;
	page: number;
	pageSize: number;
	orderBy?: string;
	filterPayload?: FilterPayload[];
}

export interface InterestRateData extends TimeStamp, TimeStampInfoType {
	id: number;
	dateFrom: string;
	dateTo: string;
	yield: number;
	intCredit: number;
	isCurrent: boolean;
	archived: boolean;
}

interface CreatePayload {
	yield?: number;
	intCredit?: number;
	dateFrom?: string;
	dateTo?: string;
	isCurrent?: boolean;
}

const useInterestRate = () => {
	const { api } = useApi();
	const { setSimpleErrorSnack } = useContext(GlobalContexts);

	const getInterestRate = async ({
		interestRate,
		setInterestRate,
		page,
		pageSize,
		orderBy,
		filterPayload,
	}: GetInterestRateProps) => {
		try {
			setInterestRate({
				...interestRate,
				loading: true,
			});
			let filter = '';
			filterPayload?.map((item) => {
				filter = filter.concat(
					`&Filters=field=${item.field}${item?.value ? `;value=${item.value.join(',')}` : ''}${
						item?.query ? `;query=${item.query}` : ''
					}${item?.from ? `;from=${item.from}` : ''}${item?.to ? `;to=${item.to}` : ''}`,
				);
			});
			let response;
			if (page) {
				response = await api.get(
					`interestrate/list?PageNumber=${page + 1}&PageSize=${pageSize}${
						orderBy || '&SortBy=updated%20desc'
					}${filter}`,
				);
			} else {
				response = await api.get(
					`interestrate/list?&PageSize=${pageSize}${orderBy || '&SortBy=updated%20desc'}${filter}`,
				);
			}

			setInterestRate({
				...interestRate,
				loading: false,
				data: {
					...response?.data?.result,
					totalRecords: response?.data?.result?.totalRecords || interestRate.data?.totalRecords,
				},
				error: null,
			});
		} catch (err) {
			if (Axios.isAxiosError(err)) {
				setInterestRate({
					...interestRate,
					loading: false,
					error: errorCodes(err?.response?.data) as ResponseError,
				});
			}
			setSimpleErrorSnack({
				message: 'An error occured',
				severity: 'error',
				show: true,
			});
		}
	};

	const createInterestRate = async (payload: CreatePayload): Promise<boolean> => {
		try {
			const createdData = await api.post(`interestrate/create`, {
				...payload,
			});
			if (createdData?.data?.result) {
				setSimpleErrorSnack({
					message: 'Intrest rate created successfully',
					severity: 'success',
					show: true,
				});
				return true;
			}
			return false;
		} catch (error) {
			setSimpleErrorSnack({
				message: 'An error occured',
				severity: 'error',
				show: true,
			});
			return false;
		}
	};

	const updateInterestRate = async (payload: CreatePayload, id: number): Promise<boolean> => {
		try {
			const updatedData = await api.put(`interestrate/${id}/update`, payload);
			if (updatedData?.data?.statusCode === 0) {
				setSimpleErrorSnack({
					message: 'Interest rate updated successfully',
					severity: 'success',
					show: true,
				});
				return true;
			}
			return false;
		} catch (error) {
			setSimpleErrorSnack({
				message: 'An error occured',
				severity: 'error',
				show: true,
			});
			return false;
		}
	};

	const deleteInterestRate = async (id: number): Promise<boolean> => {
		try {
			const deletedData = await api.remove(`interestrate/${id}/archive`);
			if (deletedData?.data?.statusCode === 0) {
				setSimpleErrorSnack({
					message: 'Interest rate deleted successfully',
					severity: 'success',
					show: true,
				});
				return true;
			}
			return false;
		} catch (error) {
			setSimpleErrorSnack({
				message: 'An error occured',
				severity: 'error',
				show: true,
			});
			return false;
		}
	};

	return {
		getInterestRate,
		createInterestRate,
		updateInterestRate,
		deleteInterestRate,
	};
};

export default useInterestRate;
